/**
 * Storage Service
 * Handles localStorage/cookie operations for onboarding state
 * 
 * User-specific data is stored with user ID prefix to prevent
 * data leakage between different accounts on the same device.
 */

const STORAGE_KEYS = {
  ONBOARDING_COMPLETED: 'drug_onboarding_completed',
  ONBOARDING_STEP: 'drug_onboarding_step',
  LAUNCH_INDEX: 'drug_launch_index',
  USER_ROLE: 'drug_user_role',
  AD_DRAFT: 'drug_ad_draft',
  SIGNUP_DRAFT: 'drug_signup_draft',
  CURRENT_USER_ID: 'drug_current_user_id',
} as const;

export type UserRole = 'adopter' | 'curator';

const TOTAL_ONBOARDING_STEPS = 5;

export interface OnboardingState {
  completed: boolean;
  step: number;
  totalSteps: number;
}

/**
 * Get user-specific storage key
 */
function getUserKey(baseKey: string, userId?: string | null): string {
  if (!userId) return baseKey;
  return `${baseKey}_${userId}`;
}

/**
 * Get current user ID from storage
 */
function getCurrentUserId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEYS.CURRENT_USER_ID);
  } catch {
    return null;
  }
}

export const storageService = {
  /**
   * Set current user ID - call this on login/session restore
   */
  setCurrentUserId(userId: string | null): void {
    try {
      const previousUserId = getCurrentUserId();
      
      // If user changed, we should NOT auto-migrate data
      // Just update the current user ID
      if (userId) {
        localStorage.setItem(STORAGE_KEYS.CURRENT_USER_ID, userId);
      } else {
        localStorage.removeItem(STORAGE_KEYS.CURRENT_USER_ID);
      }
      
      // Clear non-user-specific caches if user changed (to prevent data leakage)
      if (previousUserId && previousUserId !== userId) {
        // Clear old user role cache
        localStorage.removeItem(STORAGE_KEYS.USER_ROLE);
      }
    } catch {
      // Silent fail
    }
  },

  /**
   * Get current user ID
   */
  getCurrentUserId(): string | null {
    return getCurrentUserId();
  },

  /**
   * Clear all user-specific data (call on logout)
   */
  clearUserData(): void {
    try {
      const userId = getCurrentUserId();
      
      // Clear user-specific keys
      if (userId) {
        localStorage.removeItem(getUserKey(STORAGE_KEYS.AD_DRAFT, userId));
        localStorage.removeItem(getUserKey(STORAGE_KEYS.USER_ROLE, userId));
        localStorage.removeItem(getUserKey(STORAGE_KEYS.ONBOARDING_COMPLETED, userId));
        localStorage.removeItem(getUserKey(STORAGE_KEYS.ONBOARDING_STEP, userId));
      }
      
      // Clear non-prefixed keys (legacy)
      localStorage.removeItem(STORAGE_KEYS.AD_DRAFT);
      localStorage.removeItem(STORAGE_KEYS.USER_ROLE);
      localStorage.removeItem(STORAGE_KEYS.CURRENT_USER_ID);
    } catch {
      // Silent fail
    }
  },

  /**
   * Get onboarding state from localStorage (user-specific)
   */
  getOnboardingState(): OnboardingState {
    try {
      const userId = getCurrentUserId();
      const completedKey = getUserKey(STORAGE_KEYS.ONBOARDING_COMPLETED, userId);
      const stepKey = getUserKey(STORAGE_KEYS.ONBOARDING_STEP, userId);
      
      const completed = localStorage.getItem(completedKey);
      const stepStr = localStorage.getItem(stepKey);
      const step = stepStr ? parseInt(stepStr, 10) : 0;

      return {
        completed: completed === 'true' || step >= TOTAL_ONBOARDING_STEPS,
        step: step || 0,
        totalSteps: TOTAL_ONBOARDING_STEPS,
      };
    } catch {
      return { completed: false, step: 0, totalSteps: TOTAL_ONBOARDING_STEPS };
    }
  },

  /**
   * Set onboarding step (user-specific)
   */
  setOnboardingStep(step: number): void {
    try {
      const userId = getCurrentUserId();
      const completedKey = getUserKey(STORAGE_KEYS.ONBOARDING_COMPLETED, userId);
      const stepKey = getUserKey(STORAGE_KEYS.ONBOARDING_STEP, userId);
      
      localStorage.setItem(stepKey, step.toString());
      if (step >= TOTAL_ONBOARDING_STEPS) {
        localStorage.setItem(completedKey, 'true');
      }
    } catch {
      // Silent fail for localStorage errors
    }
  },

  /**
   * Mark onboarding as completed (user-specific)
   */
  completeOnboarding(): void {
    try {
      const userId = getCurrentUserId();
      const completedKey = getUserKey(STORAGE_KEYS.ONBOARDING_COMPLETED, userId);
      const stepKey = getUserKey(STORAGE_KEYS.ONBOARDING_STEP, userId);
      
      localStorage.setItem(completedKey, 'true');
      localStorage.setItem(stepKey, TOTAL_ONBOARDING_STEPS.toString());
    } catch {
      // Silent fail
    }
  },

  /**
   * Get launch index (how many times app has been launched) - global, not user-specific
   */
  getLaunchIndex(): number {
    try {
      const index = localStorage.getItem(STORAGE_KEYS.LAUNCH_INDEX);
      return index ? parseInt(index, 10) : 0;
    } catch {
      return 0;
    }
  },

  /**
   * Increment and return launch index
   */
  incrementLaunchIndex(): number {
    try {
      const current = this.getLaunchIndex();
      const next = current + 1;
      localStorage.setItem(STORAGE_KEYS.LAUNCH_INDEX, next.toString());
      return next;
    } catch {
      return 1;
    }
  },

  /**
   * Check if this is first launch (no launch index stored)
   */
  isFirstLaunch(): boolean {
    try {
      return localStorage.getItem(STORAGE_KEYS.LAUNCH_INDEX) === null;
    } catch {
      return true;
    }
  },

  /**
   * Check if session cookie/token exists
   */
  hasSessionCookie(): boolean {
    try {
      // Check for Supabase auth token in localStorage
      const keys = Object.keys(localStorage);
      return keys.some(key => key.includes('supabase') && key.includes('auth'));
    } catch {
      return false;
    }
  },

  /**
   * Set user role in localStorage (user-specific)
   * 
   * SECURITY NOTE: This is for UI caching/display purposes ONLY.
   * Never use this value for access control decisions.
   * All authorization checks MUST use server-side validation via useUserRole hook
   * which fetches roles from the database using has_role() / get_user_role() functions.
   */
  setUserRole(role: UserRole): void {
    try {
      const userId = getCurrentUserId();
      const key = getUserKey(STORAGE_KEYS.USER_ROLE, userId);
      localStorage.setItem(key, role);
    } catch {
      // Silent fail
    }
  },

  /**
   * Get cached user role from localStorage (user-specific)
   * 
   * SECURITY NOTE: This is for UI caching/display purposes ONLY.
   * Never use this value for access control decisions.
   * All authorization checks MUST use server-side validation via useUserRole hook.
   */
  getUserRole(): UserRole | null {
    try {
      const userId = getCurrentUserId();
      const key = getUserKey(STORAGE_KEYS.USER_ROLE, userId);
      const role = localStorage.getItem(key);
      if (role === 'adopter' || role === 'curator') {
        return role;
      }
      return null;
    } catch {
      return null;
    }
  },

  /**
   * Save ad draft to localStorage (user-specific)
   */
  saveAdDraft(draft: AdDraft): void {
    try {
      const userId = getCurrentUserId();
      const key = getUserKey(STORAGE_KEYS.AD_DRAFT, userId);
      const draftWithTimestamp = {
        ...draft,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(key, JSON.stringify(draftWithTimestamp));
    } catch {
      // Silent fail
    }
  },

  /**
   * Get ad draft from localStorage (user-specific)
   */
  getAdDraft(): AdDraftWithTimestamp | null {
    try {
      const userId = getCurrentUserId();
      const key = getUserKey(STORAGE_KEYS.AD_DRAFT, userId);
      const data = localStorage.getItem(key);
      if (!data) return null;
      return JSON.parse(data) as AdDraftWithTimestamp;
    } catch {
      return null;
    }
  },

  /**
   * Check if there's a saved ad draft (user-specific)
   */
  hasAdDraft(): boolean {
    try {
      const userId = getCurrentUserId();
      const key = getUserKey(STORAGE_KEYS.AD_DRAFT, userId);
      return localStorage.getItem(key) !== null;
    } catch {
      return false;
    }
  },

  /**
   * Clear ad draft from localStorage (user-specific)
   */
  clearAdDraft(): void {
    try {
      const userId = getCurrentUserId();
      const key = getUserKey(STORAGE_KEYS.AD_DRAFT, userId);
      localStorage.removeItem(key);
    } catch {
      // Silent fail
    }
  },

  /**
   * Save signup draft to localStorage (email, password hash, checkboxes)
   * NOTE: Password is NOT stored in plain text for security
   * This is NOT user-specific (used before login)
   */
  saveSignupDraft(draft: SignupDraft): void {
    try {
      localStorage.setItem(STORAGE_KEYS.SIGNUP_DRAFT, JSON.stringify(draft));
    } catch {
      // Silent fail
    }
  },

  /**
   * Get signup draft from localStorage
   */
  getSignupDraft(): SignupDraft | null {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.SIGNUP_DRAFT);
      if (!data) return null;
      return JSON.parse(data) as SignupDraft;
    } catch {
      return null;
    }
  },

  /**
   * Clear signup draft from localStorage (after successful registration)
   */
  clearSignupDraft(): void {
    try {
      localStorage.removeItem(STORAGE_KEYS.SIGNUP_DRAFT);
    } catch {
      // Silent fail
    }
  },
};

// Type for signup draft
export interface SignupDraft {
  email: string;
  password: string;
  agreementChecked: boolean;
  adsConsentChecked: boolean;
}

// Type for health data in ad draft
export interface HealthData {
  has_special_needs: boolean | null;
  category: string | null;
  description: string | null;
}

// Type for ad draft
export interface AdDraft {
  species_key: 'dog' | 'cat' | null;
  breed: string;
  breed_id: string | null;
  name: string;
  sex: 'female' | 'male' | null;
  age_group_id: string | null; // ID возрастной группы ('trainee', 'junior', 'middle', 'senior', 'boss')
  character: number;
  gets_along_with: string[];
  interesting_fact: string;
  interesting_fact_source: 'user' | 'generated';
  photos: string[];
  step: 'welcome' | 'species' | 'form' | 'characteristics' | 'appearance' | 'health' | 'additional_info' | 'photos';
  // ADS-002-5: Внешний вид
  size: string | null; // 'xs' | 's' | 'm' | 'l' — single-select
  color: string | null; // 'white' | 'red' | 'black' | 'tortie' | 'gray' | 'other'
  // ADS-002-6: Здоровье питомца
  health: HealthData;
  // ADS-002-7.1: Дополнительная информация
  additional_info: string;
}

export interface AdDraftWithTimestamp extends AdDraft {
  savedAt: string;
}

export default storageService;
